import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, RefreshCw } from 'lucide-react';
import { listEvaluations, getEvaluationStats } from '../../services/adminService';
import { qualityScores, evaluationStatuses } from '../../utils/qualityConstants';
import { googleSupportedLanguages } from '../../services/languageService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import LoadingSpinner from '../common/LoadingSpinner';

const DashboardCard = ({ title, value, icon: Icon }) => {
  if (Array.isArray(value)) {
    return (
      <div className="bg-white p-6 rounded-lg border">
        <div className="flex items-start justify-between">
          <div className="w-full">
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <div className="space-y-1">
              {value.map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <span className="text-gray-600 capitalize">{item.name}</span>
                  <span className="text-1xl font-semibold">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
          {Icon && <Icon className="text-gray-400" size={20} />}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg border">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-semibold">{value}</p>
        </div>
        {Icon && <Icon className="text-gray-400" size={20} />}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);

  const fetchStats = async () => {
    try {
      const stats = await getEvaluationStats();
      setStats(stats)
    } catch (error) {
      console.error('Failed to fetch evaluation statistics:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div className="grid grid-cols-4 gap-4">
      <DashboardCard
        title="Queue Status"
        value={[
          {
            name: "Queued",
            value: `${stats?.queued || 0}`
          },  
          {
            name: "Draft",
            value: `${stats?.draft || 0}`
          },
          {
            name: "In progress",
            value: `${stats?.inProgress || 0}`
          },
          {
            name: "Completed",
            value: `${stats?.completed || 0}`
          }
        ]}
      />
      <DashboardCard
        title="Total Evaluations"
        value={`${stats?.total || 0}`}
      />
      <DashboardCard
        title="Total Price"
        value={`$${stats?.totalPrice || 0}`}
      />
      <DashboardCard
        title="Tokens Used"
        value={`${(stats?.totalTokensIn || 0) + (stats?.totalTokensOut || 0)}`}
      />
    </div>
  );
};

const EvaluationsList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSearchQuery, setTempSearchQuery] = useState('');
  const [statusSearchQuery, setStatusSearchQuery] = useState(undefined);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;
  const [error, setError] = useState(null);

  const fetchEvaluations = async () => {
    try {
      setLoading(true);
      const response = await listEvaluations({
        pageSize: pageSize,
        pageNumber: currentPage - 1,
        status: statusSearchQuery,
        searchTerm: searchQuery || undefined
      });
      setEvaluations(response.evaluations);
      setPageCount(response.pageCount);
    } catch (error) {
      console.error('Failed to fetch evaluations:', error);
      setError('Failed to load evaluations');
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvaluations();
  // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [searchQuery, currentPage, statusSearchQuery]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-violet-100';
      case 'QUEUED':
        return 'bg-purple-100';
      case 'IN_PROGRESS':
        return 'bg-blue-100';
      case 'COMPLETED':
        return 'bg-green-100';
      case 'ABORTED':
        return 'bg-yellow-100';
      case 'ERROR':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(Number(timestamp.seconds) * 1000);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const getQualityCategory = (score) => {
    const category = qualityScores.find(cat => {
      const [min, max] = cat.score.split('-').map(Number);
      return score >= min && score <= max;
    });
    return category || qualityScores[qualityScores.length - 1];
  };

  const calculateCompletionTime = (started, finished) => {
    if (!started || !finished) return '-';
    const startTime = Number(started.seconds) * 1000;
    const finishTime = Number(finished.seconds) * 1000;
    const diffInSeconds = (finishTime - startTime) / 1000;
    
    if (diffInSeconds < 0) return '-';
    
    const minutes = Math.floor(diffInSeconds / 60);
    const seconds = Math.floor(diffInSeconds % 60);
    
    if (minutes === 0) {
      return `${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  };

  const getLanguageName = (code) => {
    const language = googleSupportedLanguages.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  const renderPaginationButtons = () => {
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(pageCount, startPage + maxVisiblePages - 1);

    // Adjust start and end if we're near the beginning or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = [];
    
    // Always show first page
    pages.push(
      <button
        key={1}
        onClick={() => setCurrentPage(1)}
        className={`px-3 py-1 border rounded ${
          currentPage === 1
            ? 'bg-[rgb(17,24,39)] text-white'
            : 'hover:bg-gray-50'
        }`}
        disabled={loading}
      >
        1
      </button>
    );

    if (startPage > 2) {
      pages.push(
        <span key="start-ellipsis" className="px-2">
          ...
        </span>
      );
    }

    for (let i = Math.max(2, startPage); i <= Math.min(pageCount - 1, endPage); i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 border rounded ${
            currentPage === i
              ? 'bg-[rgb(17,24,39)] text-white'
              : 'hover:bg-gray-50'
          }`}
          disabled={loading}
        >
          {i}
        </button>
      );
    }

    // Add second ellipsis if needed
    if (endPage < pageCount - 1) {
      pages.push(
        <span key="end-ellipsis" className="px-2">
          ...
        </span>
      );
    }

    // Always show last page if there is more than one page
    if (pageCount > 1) {
      pages.push(
        <button
          key={pageCount}
          onClick={() => setCurrentPage(pageCount)}
          className={`px-3 py-1 border rounded ${
            currentPage === pageCount
              ? 'bg-[rgb(17,24,39)] text-white'
              : 'hover:bg-gray-50'
          }`}
          disabled={loading}
        >
          {pageCount}
        </button>
      );
    }

    return (
      <div className="flex items-center space-x-2">
        <button
          className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>
        {pages}
        <button
          className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount))}
          disabled={currentPage === pageCount || loading}
        >
          Next
        </button>
      </div>
    );
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchQuery(tempSearchQuery);
      setCurrentPage(1);
    }
  };

  const handleRefresh = () => {
    fetchEvaluations();
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-red-500">
        <AlertCircle className="mr-2" size={20} />
        {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-8">
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>
      
      <div className="flex items-center space-x-4 mb-6">
          <div className="w-[200px]">
          <Select 
            value={statusSearchQuery || 'all'} 
            onValueChange={(value) => setStatusSearchQuery(value === 'all' ? undefined : Number(value))}
          >
            <SelectTrigger className="w-full">
              <SelectValue>
                {statusSearchQuery 
                  ? evaluationStatuses[statusSearchQuery].label
                  : "All Statuses"
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={undefined}>All</SelectItem>
              <SelectItem value="1">Draft</SelectItem>
              <SelectItem value="2">Queued</SelectItem>
              <SelectItem value="3">In Progress</SelectItem>
              <SelectItem value="4">Completed</SelectItem>
              <SelectItem value="6">Error</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search evaluations..."
            value={tempSearchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
        <button
          onClick={handleRefresh}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          title="Refresh"
        >
          <RefreshCw size={20} />
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.1)]">
        <div style={{ 
          padding: '0px 0px 1rem',
          marginBottom: '1rem'
        }}>
          <div className="overflow-x-scroll" style={{ 
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: '-ms-autohiding-scrollbar'
          }}>
            <style>
              {`
                .scroll-container::-webkit-scrollbar {
                  -webkit-appearance: none;
                  height: 4px;
                }
                .scroll-container::-webkit-scrollbar-track {
                  background-color: rgba(0, 0, 0, .05);
                }
                .scroll-container::-webkit-scrollbar-thumb {
                  border-radius: 2px;
                  background-color: rgba(0, 0, 0, .2);
                }
              `}
            </style>
            <div className="scroll-container rounded-lg overflow-hidden shadow" style={{ overflowX: 'scroll', paddingBottom: '1rem' }}>
              <table className="min-w-[1400px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px]">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[160px]">Email</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Created</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Started</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Finished</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Completion</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Source</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Target</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Segments</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Model</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Tokens</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Price</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">Score</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="15" className="px-4 pt-6 py-4 text-center">
                        <LoadingSpinner size="medium" />
                      </td>
                    </tr>
                  ) : evaluations.length === 0 ? (
                    <tr>
                      <td colSpan="15" className="px-4 pt-6 py-4 text-center text-sm text-gray-500">
                        No results found
                      </td>
                    </tr>
                  ) : (
                    evaluations.map((evaluation) => {
                      const qualityCategory = getQualityCategory(evaluation.overallScore);
                      return (
                        <tr key={evaluation.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 overflow-hidden text-ellipsis">
                            <a href={`/evaluate/${evaluation.id}`} target='_blank' className="text-[rgb(17,24,39)] hover:text-[rgb(17,24,39)]/80" rel="noreferrer">
                              {evaluation.id.slice(0, 8)}...
                            </a>
                          </td>
                          <td className="max-w-[200px] px-4 py-2 whitespace-nowrap text-sm overflow-hidden text-ellipsis">
                            {evaluation.name || 'Untitled'}
                          </td>
                          <td className="max-w-[160px] px-4 py-2 whitespace-nowrap text-center text-sm overflow-hidden text-ellipsis">
                            {evaluation.email || '-'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(evaluation.status)} ${evaluationStatuses[evaluation.status].color}`}>
                              {evaluationStatuses[evaluation.status].label}
                              {evaluation.status === 'ERROR' && (
                                <AlertCircle className="ml-1 w-4 h-4" />
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(evaluation.createdAt)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(evaluation.evaluationStarted)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(evaluation.evaluationFinished)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 text-center">
                            {calculateCompletionTime(evaluation.evaluationStarted, evaluation.evaluationFinished)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {getLanguageName(evaluation.sourceLanguage)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {getLanguageName(evaluation.targetLanguage)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                            {evaluation.statistics?.totalSegments || 0}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                            {evaluation.model || '-'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                            {(evaluation.tokensInUsed && evaluation.tokensOutUsed) 
                              ? (evaluation.tokensInUsed + evaluation.tokensOutUsed).toLocaleString()
                              : '-'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-center">
                            {evaluation.price ? evaluation.price.toFixed(2) : '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-center">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${qualityCategory.color}`}>
                              {evaluation.overallScore || 0}
                            </span>
                          </td>

                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center space-x-2">
                              <button 
                                className="text-blue-600 hover:text-blue-800"
                                onClick={() => window.open(`/evaluate/${evaluation.id}`, '_blank')}
                              >
                                View
                              </button>
                              {/* <span className="text-gray-300">|</span> */}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing page {currentPage} of {pageCount || 1}
        </div>
        {renderPaginationButtons()}
      </div>
    </div>
  );
};

const AdminPage = () => {
  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
      </div>
      <Dashboard />
      <EvaluationsList />
    </div>
  );
};

export default AdminPage; 